/**
 * Speech URL Mode - Word Timestamp Display
 * 
 * Displays speech transcripts and AI responses using word timestamps from TTS.
 * Text follows audio playback position for easier reading.
 */

import { bus } from '../../core/events.js';
import type { ChatMessage } from '../../services/llm/index.js';
import { speak, getCurrentWordTimestamps, getCurrentAudioElement, type WordTimestamp } from '../../services/tts/index.js';
import { log } from '../../services/logging/index.js';
import { 
  isWindowFocusedNow, 
  shouldAskAboutBackgroundConversation, 
  isBackgroundConversationAllowed,
  setBackgroundConversationPreference,
  markAskedAboutBackgroundConversationToday
} from '../../services/window-focus/index.js';
import { state } from '../../core/state.js';

export interface SpeechState {
  transcript: string;
  aiResponse: string;
  isListening: boolean;
  lastUpdate: number;
  maxLength: number;
  waveform: number[];
  maxWaveformLength: number;
  
  // Word timestamp display state
  displayText: string; // Full text being displayed
  displaySource: 'user' | 'ai' | null; // Which source is currently displayed
  wordTimestamps: WordTimestamp[] | null; // Word timestamps from TTS
  updateTimer: ReturnType<typeof setInterval> | null; // Timer for updating display based on audio position
  lastWordIndex: number; // Last word index for tracking word changes
  
  // Conversation history
  conversationHistory: ChatMessage[]; // Accumulated conversation (user and assistant messages)
  
  // Processing state
  isProcessingLLM: boolean;
  isTTSActive: boolean;
  ignoreTranscriptsUntil: number;
  currentLLMRequestId: number;
  
  // Language detection
  detectedLanguage: string | null; // Detected language code (e.g., 'en', 'es', 'fr')
  languageConfidence: number | null; // Language detection confidence (0.0 to 1.0)
}

/**
 * Create speech state
 */
export function createSpeechState(): SpeechState {
  return {
    transcript: '',
    aiResponse: '',
    isListening: false,
    lastUpdate: 0,
    maxLength: 10000, // Increased to allow full transcripts and AI responses (only used for URL display, not truncation)
    waveform: [],
    maxWaveformLength: 60,
    
    // Word timestamp display state
    displayText: '',
    displaySource: null,
    wordTimestamps: null,
    updateTimer: null,
    lastWordIndex: -1,
    
    // Conversation history
    conversationHistory: [], // Start with empty conversation
    
    // Processing state
    isProcessingLLM: false,
    isTTSActive: false,
    ignoreTranscriptsUntil: 0,
    currentLLMRequestId: 0,
    
    // Window focus state
    waitingForWindowFocusResponse: false,
    
    // Language detection
    detectedLanguage: null,
    languageConfidence: null,
  };
}

const DISPLAY_SIZE = 50; // Fixed 50-character display window

/**
 * Update display based on current audio playback position
 */
function updateDisplayFromAudio(
  state: SpeechState,
  onUpdate: () => void
): void {
  const audio = getCurrentAudioElement();
  const wordTimestamps = getCurrentWordTimestamps();
  
  // If no audio or timestamps, show static text
  if (!audio || !wordTimestamps || wordTimestamps.length === 0) {
    // Show static text if available
    if (state.displayText) {
      const cleanText = state.displayText.replace(/\s+/g, '_');
      if (cleanText.length <= DISPLAY_SIZE) {
        // Text fits - just display it
        onUpdate();
        return;
      }
      // Show first 50 characters
      onUpdate();
    }
    return;
  }
  
  // Check if audio has ended or is paused
  if (audio.paused || audio.ended) {
    // Audio has ended - stop the timer and show final text
    stopWordDisplay(state);
    // Show the last words
    const lastWords = wordTimestamps.slice(-10); // Last 10 words
    const displayText = lastWords.map(w => w.word).join(' ').replace(/\s+/g, '_');
    state.displayText = displayText.padEnd(DISPLAY_SIZE, ' ');
    onUpdate();
    return;
  }
  
  // Get current playback time
  const currentTime = audio.currentTime;
  
  // Track previous word index to detect changes
  const previousWordIndex = state.lastWordIndex;
  
  // Find current word being spoken
  let currentWordIndex = -1;
  for (let i = 0; i < wordTimestamps.length; i++) {
    const word = wordTimestamps[i];
    if (currentTime >= word.start && currentTime <= word.end) {
      currentWordIndex = i;
      break;
    }
  }
  
  // If no word is currently being spoken, find the last word that was spoken
  if (currentWordIndex === -1) {
    for (let i = wordTimestamps.length - 1; i >= 0; i--) {
      if (currentTime >= wordTimestamps[i].end) {
        currentWordIndex = i;
        break;
      }
    }
    // If before first word, use first word
    if (currentWordIndex === -1 && wordTimestamps.length > 0) {
      currentWordIndex = 0;
    }
  }
  
  // Log word stream when word changes
  if (currentWordIndex !== previousWordIndex && currentWordIndex >= 0) {
    const currentWord = wordTimestamps[currentWordIndex];
    console.log('[TTS Word Stream]', {
      word: currentWord.word,
      index: currentWordIndex,
      start: currentWord.start,
      end: currentWord.end,
      currentTime: currentTime.toFixed(3),
      progress: ((currentTime / currentWord.end) * 100).toFixed(1) + '%'
    });
    // Store last word index for next comparison
    state.lastWordIndex = currentWordIndex;
  }
  
  // Build display text from words, centered around current word
  if (currentWordIndex >= 0) {
    // Collect words to display (current word + surrounding words to fill 50 chars)
    const words: string[] = [];
    let totalLength = 0;
    
    // Start from current word and work backwards to find starting point
    let startIndex = currentWordIndex;
    for (let i = currentWordIndex; i >= 0; i--) {
      const word = wordTimestamps[i].word;
      const wordWithSpace = (i < currentWordIndex ? ' ' : '') + word;
      if (totalLength + wordWithSpace.length > DISPLAY_SIZE) {
        break;
      }
      startIndex = i;
      totalLength += wordWithSpace.length;
    }
    
    // Now collect words forward from startIndex
    const displayWords: string[] = [];
    totalLength = 0;
    for (let i = startIndex; i < wordTimestamps.length; i++) {
      const word = wordTimestamps[i].word;
      const wordWithSpace = (i > startIndex ? ' ' : '') + word;
      if (totalLength + wordWithSpace.length > DISPLAY_SIZE) {
        break;
      }
      displayWords.push(word);
      totalLength += wordWithSpace.length;
    }
    
    // Join words and replace spaces with underscores for URL
    const displayText = displayWords.join(' ').replace(/\s+/g, '_');
    
    // Pad to DISPLAY_SIZE if needed
    const paddedText = displayText.padEnd(DISPLAY_SIZE, ' ');
    
    // Update state if changed
    if (state.displayText !== paddedText) {
      state.displayText = paddedText;
      onUpdate();
    }
  }
}

/**
 * Start word timestamp-based display
 */
function startWordDisplay(
  state: SpeechState,
  text: string,
  source: 'user' | 'ai',
  onUpdate: () => void
): void {
  // Stop any existing timer
  stopWordDisplay(state);
  
  // Replace spaces with underscores for URL
  const cleanText = text.replace(/\s+/g, '_');
  
  // Set display text and source
  state.displayText = cleanText.length <= DISPLAY_SIZE 
    ? cleanText.padEnd(DISPLAY_SIZE, ' ')
    : cleanText.slice(0, DISPLAY_SIZE);
  state.displaySource = source;
  
  // Get word timestamps from TTS (if available)
  // If not already set, try to get them
  if (!state.wordTimestamps) {
    state.wordTimestamps = getCurrentWordTimestamps();
  }
  
  // If we have word timestamps, start updating display based on audio position
  // The update function will handle cases where audio hasn't started yet
  if (state.wordTimestamps && state.wordTimestamps.length > 0) {
    // Update display periodically based on audio playback
    state.updateTimer = setInterval(() => {
      updateDisplayFromAudio(state, onUpdate);
    }, 100); // Update every 100ms
  }
  
  onUpdate();
}

/**
 * Stop word timestamp display
 */
function stopWordDisplay(state: SpeechState): void {
  if (state.updateTimer) {
    clearInterval(state.updateTimer);
    state.updateTimer = null;
  }
}

/**
 * Switch display to new source (user or AI)
 */
function switchDisplaySource(
  state: SpeechState,
  text: string,
  source: 'user' | 'ai',
  onUpdate: () => void
): void {
  // Stop current display
  stopWordDisplay(state);
  
  // Clear display briefly (optional - for clean transition)
  state.displayText = '';
  state.displaySource = null;
  state.wordTimestamps = null;
  onUpdate();
  
  // Start new display after brief pause
  setTimeout(() => {
    startWordDisplay(state, text, source, onUpdate);
  }, 200); // 200ms pause for clean transition
}

/**
 * Initialize speech state and listen for voice events
 */
export function initializeSpeech(state: SpeechState, onUpdate: () => void): void {
  // Listen for TTS stop requests (e.g., from interruption detection)
  bus.on('tts.stop.requested', (payload: unknown) => {
    const data = payload as { reason: string; word?: string; timestamp: number };
    
    // Stop TTS immediately
    import('../../services/tts/index.js').then(({ stop: stopTTS }) => {
      stopTTS();
      state.isTTSActive = false;
      state.ignoreTranscriptsUntil = Date.now() + 500; // Short cooldown
      onUpdate();
    });
  });
  
  // Listen for window focus changes to clear waiting flag when window regains focus
  bus.on('window.focus.changed', (payload: unknown) => {
    const data = payload as { isFocused: boolean; timestamp: number };
    if (data.isFocused && state.waitingForWindowFocusResponse) {
      state.waitingForWindowFocusResponse = false;
      onUpdate();
    }
  });
  
  // Listen for user.voice events from the microphone
  bus.on('user.voice', async (payload: unknown) => {
    const data = payload as { 
      transcript: string; 
      timestamp: number; 
      _test?: boolean;
      languageCode?: string | null;
      languageConfidence?: number | null;
    };
    
    // Ignore test events - these are for visualization only
    if (data._test) {
      return;
    }
    
    // Prevent feedback loop: ignore transcripts while TTS is active or during cooldown period
    const now = Date.now();
    if (state.isTTSActive || now < state.ignoreTranscriptsUntil) {
      return;
    }
    
    // Note: Drawing handler operates in parallel - it doesn't intercept
    // The main Decision Engine conversation continues normally
    // Drawing handler evaluates opportunities separately and enriches when appropriate
    
    if (data.transcript) {
      // Capture the transcript from the event payload
      // Don't truncate - use full transcript for LLM and conversation
      const currentTranscript = data.transcript.trim();
      
      // Store language metadata if available
      if (data.languageCode) {
        state.detectedLanguage = data.languageCode;
        state.languageConfidence = data.languageConfidence || null;
      }
      
      // Update state (keep full transcript, don't truncate)
      state.transcript = currentTranscript;
      state.lastUpdate = Date.now();
      
      // Switch display to user speech (clean switch, no mixing)
      // Use full transcript for display (it will handle display truncation)
      switchDisplaySource(state, currentTranscript, 'user', onUpdate);
      
      // Add user message to conversation history (full transcript, not truncated)
      state.conversationHistory.push({
        role: 'user',
        content: currentTranscript
      });
      
      // Check if window is not focused and we should ask about background conversation
      
      const windowFocused = isWindowFocusedNow();
      const backgroundAllowed = isBackgroundConversationAllowed();
      
      // Always clear waiting flag if window is focused (fixes stuck state)
      if (windowFocused && state.waitingForWindowFocusResponse) {
        state.waitingForWindowFocusResponse = false;
      }
      
      // If we're already waiting for a response to the window focus question, use the window-not-focused prompt
      // This ensures the AI can see the conversation history and understand the user's response
      // Only use window-not-focused prompt if window is actually not focused AND we're not already waiting
      const useWindowNotFocusedPrompt = (!windowFocused && !backgroundAllowed && !state.waitingForWindowFocusResponse) || 
                                        (state.waitingForWindowFocusResponse && !windowFocused);
      
      if (useWindowNotFocusedPrompt && !windowFocused && !backgroundAllowed) {
        // Set flag that we're waiting for a response (only if this is the first ask, not a follow-up)
        if (!state.waitingForWindowFocusResponse) {
          state.waitingForWindowFocusResponse = true;
        }
      }
      
      // Send to LLM in parallel with full conversation history
      const requestId = ++state.currentLLMRequestId;
      state.isProcessingLLM = true;
      onUpdate();
      
      (async () => {
        try {
          // Get dragged files/cards context
          const { dragDropContext } = await import('../../systems/drag-drop-context.js');
          const dragContext = dragDropContext.getContext();
          const contextSummary = dragDropContext.getContextSummary();
          
          // Split context into files and cards
          const draggedFiles = dragContext.files.length > 0 ? contextSummary : undefined;
          const draggedCards = dragContext.cards.length > 0 ? contextSummary : undefined;

          // Check if user is on drawing screen
          const { getDrawingState } = await import('../../services/drawing/index.js');
          const drawingState = getDrawingState();
          const isDrawingScreen = drawingState.active;

          // Use Cortex API instead of direct OpenAI calls
          const { streamCortex } = await import('../../services/cortex-api/client.js');
          
          // Build context for Cortex API
          const cortexContext: Record<string, any> = {
            windowNotFocused: useWindowNotFocusedPrompt,
            draggedFiles: draggedFiles,
            draggedCards: draggedCards,
            isDrawingScreen: isDrawingScreen,
            languageCode: state.detectedLanguage || null,
            languageConfidence: state.languageConfidence || null,
            conversationHistory: state.conversationHistory.slice(0, -1), // Include history
          };
          
          // Track if we've received the conversational response
          let conversationalResponseReceived = false;
          let aiResponseText = '';
          let streamAbortController: AbortController | null = null;
          
          // Stream cortex request (conversation, decision, file analysis, etc.)
          streamAbortController = await streamCortex(
            {
              userInput: currentTranscript,
              context: cortexContext,
            },
            {
              onConversational: async (event) => {
                // First conversational response (1-2 seconds)
                if (!conversationalResponseReceived && requestId === state.currentLLMRequestId) {
                  conversationalResponseReceived = true;
                  aiResponseText = event.message;
                  
                  // Update state immediately with conversational response
                  state.aiResponse = aiResponseText;
                  state.conversationHistory.push({
                    role: 'assistant',
                    content: aiResponseText
                  });
                  
                  // Switch display to AI response
                  switchDisplaySource(state, aiResponseText, 'ai', onUpdate);
                  
                  // Start TTS for conversational response
                  state.isTTSActive = true;
                  const now = Date.now();
                  const estimatedDuration = (aiResponseText.split(/\s+/).length / 150) * 60 * 1000;
                  state.ignoreTranscriptsUntil = now + estimatedDuration + 2000;
                  
                  const volume = useWindowNotFocusedPrompt ? 0.35 : 1.0;
                  const { getLanguageConfig } = await import('../../services/wake-word/languages.js');
                  const detectedLanguage = state.detectedLanguage || 'en';
                  const languageConfig = getLanguageConfig(detectedLanguage);
                  const ttsVoice = languageConfig.ttsVoice || 'lily';
                  
                  speak(aiResponseText, { 
                    voice: ttsVoice,
                    useLemonFox: true,
                    volume: volume
                  })
                    .then(() => {
                      stopWordDisplay(state);
                      state.isTTSActive = false;
                      state.ignoreTranscriptsUntil = Date.now() + 1000;
                      onUpdate();
                    })
                    .catch((error) => {
                      // Fallback to Web Speech API
                      speak(aiResponseText, { 
                        useWebSpeech: true,
                        volume: volume
                      })
                        .then(() => {
                          stopWordDisplay(state);
                          state.isTTSActive = false;
                          state.ignoreTranscriptsUntil = Date.now() + 1000;
                          onUpdate();
                        })
                        .catch(() => {
                          stopWordDisplay(state);
                          state.isTTSActive = false;
                          state.ignoreTranscriptsUntil = Date.now() + 500;
                          onUpdate();
                        });
                    });
                  
                  // Update display with word timestamps
                  // Poll for timestamps since they might be generated asynchronously after audio metadata loads
                  let attempts = 0;
                  const checkTimestamps = () => {
                    // If we've moved on to a different state or stopped TTS, stop checking
                    if (!state.isTTSActive) return;

                    const timestamps = getCurrentWordTimestamps();
                    if (timestamps && timestamps.length > 0) {
                      state.wordTimestamps = timestamps;
                      if (state.displaySource === 'ai') {
                        startWordDisplay(state, aiResponseText, 'ai', onUpdate);
                      }
                    } else {
                      // Keep checking for up to 5 seconds (50 * 100ms)
                      attempts++;
                      if (attempts < 50) {
                         setTimeout(checkTimestamps, 100);
                      }
                    }
                  };
                  
                  setTimeout(checkTimestamps, 100);
                }
              },
              
              onCortexCompleted: (event) => {
                // Individual cortex results (optional - can show progress)
                if (requestId === state.currentLLMRequestId) {
                  log.debug(`[CortexAPI] Cortex completed: ${event.cortex}`, event);
                  // Could show progress indicator here if needed
                }
              },
              
              onInsufficientCredits: async (event) => {
                // Handle insufficient credits
                if (requestId === state.currentLLMRequestId) {
                  const { handleInsufficientCredits } = await import('../../services/balance/insufficient-credits-handler.js');
                  handleInsufficientCredits(event);
                }
              },
              
              onDecisionComplete: async (event) => {
                // Final decision result (if decision cycle ran)
                if (requestId === state.currentLLMRequestId && event.decision) {
                  log.debug('[CortexAPI] Decision complete', event.decision);
                  
                  // Extract decision message from decision result
                  // The decision object may have a recommendation, narrative, or summary
                  const decisionMessage = 
                    event.decision.decision?.recommendation ||
                    event.decision.decision?.narrative ||
                    event.decision.decision?.summary ||
                    event.decision.summary ||
                    '';
                  
                  if (decisionMessage && decisionMessage !== aiResponseText) {
                    // Update with final decision if different from conversational response
                    aiResponseText = decisionMessage;
                    state.aiResponse = aiResponseText;
                    
                    // Update conversation history with final decision
                    const lastAssistantMessage = state.conversationHistory[state.conversationHistory.length - 1];
                    if (lastAssistantMessage && lastAssistantMessage.role === 'assistant') {
                      lastAssistantMessage.content = aiResponseText;
                    } else {
                      state.conversationHistory.push({
                        role: 'assistant',
                        content: aiResponseText
                      });
                    }
                    
                    // Update display
                    switchDisplaySource(state, aiResponseText, 'ai', onUpdate);
                    
                    // Speak final decision if different from conversational response
                    if (!conversationalResponseReceived) {
                      state.isTTSActive = true;
                      const now = Date.now();
                      const estimatedDuration = (aiResponseText.split(/\s+/).length / 150) * 60 * 1000;
                      state.ignoreTranscriptsUntil = now + estimatedDuration + 2000;
                      
                      const volume = useWindowNotFocusedPrompt ? 0.35 : 1.0;
                      const { getLanguageConfig } = await import('../../services/wake-word/languages.js');
                      const detectedLanguage = state.detectedLanguage || 'en';
                      const languageConfig = getLanguageConfig(detectedLanguage);
                      const ttsVoice = languageConfig.ttsVoice || 'lily';
                      
                      speak(aiResponseText, { 
                        voice: ttsVoice,
                        useLemonFox: true,
                        volume: volume
                      })
                        .then(() => {
                          stopWordDisplay(state);
                          state.isTTSActive = false;
                          state.ignoreTranscriptsUntil = Date.now() + 1000;
                          onUpdate();
                        })
                        .catch(() => {
                          stopWordDisplay(state);
                          state.isTTSActive = false;
                          state.ignoreTranscriptsUntil = Date.now() + 500;
                          onUpdate();
                        });
                    }
                  }
                }
              },
              
              onError: (event) => {
                if (requestId === state.currentLLMRequestId) {
                  log.error('[CortexAPI] Stream error:', event);
                  
                  // Handle subscription limit errors
                  if (event.upgradeRequired) {
                    // Show upgrade notification
                    bus.emit('notification.show', {
                      message: `Decision limit reached (${event.currentCount}/${event.limit}). Upgrade to continue.`,
                      type: 'error',
                      duration: 5000
                    });
                  } else {
                    // Show generic error
                    bus.emit('notification.show', {
                      message: event.error || 'Failed to process request',
                      type: 'error',
                      duration: 5000
                    });
                  }
                  
                  state.aiResponse = '';
                  state.isProcessingLLM = false;
                  onUpdate();
                }
              },
              
              onComplete: (event) => {
                if (requestId === state.currentLLMRequestId) {
                  log.debug('[CortexAPI] Stream complete:', event.message);
                  state.isProcessingLLM = false;
                  onUpdate();
                }
              }
            }
          );
          
          // Check if this request is still current
          if (requestId !== state.currentLLMRequestId) {
            // Abort the stream if request is no longer current
            if (streamAbortController) {
              streamAbortController.abort();
            }
            return;
          }
          
          // Handle window-not-focused prompt parsing if needed
          // (This is handled in the conversational response now, but we keep the logic for JSON parsing)
          if (useWindowNotFocusedPrompt && aiResponseText) {
            try {
              let jsonStr = aiResponseText.trim();
              const jsonMatch = jsonStr.match(/```json\s*([\s\S]*?)\s*```/);
              if (jsonMatch) {
                jsonStr = jsonMatch[1].trim();
              } else {
                const firstBrace = jsonStr.indexOf('{');
                const lastBrace = jsonStr.lastIndexOf('}');
                if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
                  jsonStr = jsonStr.substring(firstBrace, lastBrace + 1);
                }
              }
              
              const responseData = JSON.parse(jsonStr);
              const isTalkingToAI = responseData.isTalkingToAI === true;
              const shouldKeepMicrophoneOn = responseData.shouldKeepMicrophoneOn === true;
              aiResponseText = responseData.message || aiResponseText;
              
              markAskedAboutBackgroundConversationToday();
              setBackgroundConversationPreference(shouldKeepMicrophoneOn);
              state.waitingForWindowFocusResponse = false;
              
              if (!shouldKeepMicrophoneOn) {
                bus.emit('microphone.stop.requested', { reason: 'user_requested', timestamp: Date.now() });
                const { stop: stopTTS } = await import('../../services/tts/index.js');
                stopTTS();
                state.isTTSActive = false;
                state.isProcessingLLM = false;
                if (streamAbortController) {
                  streamAbortController.abort();
                }
                onUpdate();
                return;
              }
              
              if (isTalkingToAI && shouldKeepMicrophoneOn) {
                state.waitingForWindowFocusResponse = false;
              }
            } catch (error) {
              // JSON parsing failed, continue with text response
            }
          }
          
          // Response handling is done in stream callbacks (onConversational, onDecisionComplete)
          // No additional processing needed here - the callbacks handle TTS and display updates
        } catch (error) {
          if (requestId === state.currentLLMRequestId) {
            state.aiResponse = '';
          }
        } finally {
          if (requestId === state.currentLLMRequestId) {
            state.isProcessingLLM = false;
            onUpdate();
          }
        }
      })();
    }
  });
  
  // Listen for microphone state changes
  bus.on('microphone.listening.changed', (payload: unknown) => {
    const data = payload as { isListening: boolean; timestamp: number };
    state.isListening = data.isListening;
    state.lastUpdate = data.timestamp;
  });
  
  bus.on('state.microphone', (payload: unknown) => {
    const micState = payload as { isListening?: boolean; isAvailable?: boolean; errorMessage?: string };
    if (micState.isListening !== undefined) {
      state.isListening = micState.isListening;
      state.lastUpdate = Date.now();
      
      if (!micState.isListening) {
        state.waveform = [];
      }
    }
  });
  
  // Listen for audio level events (for waveform visualization)
  bus.on('microphone.audio.level', (payload: unknown) => {
    const data = payload as { amplitude: number; timestamp: number };
    if (data.amplitude !== undefined && state.isListening) {
      state.waveform.push(data.amplitude);
      
      if (state.waveform.length > state.maxWaveformLength) {
        state.waveform.shift();
      }
      
      state.lastUpdate = data.timestamp;
      bus.emit('speech.waveform.updated', { timestamp: data.timestamp });
    }
  });
}

/**
 * Clear speech transcript and waveform
 */
export function clearSpeech(state: SpeechState): void {
  stopWordDisplay(state);
  
  state.transcript = '';
  state.aiResponse = '';
  state.waveform = [];
  state.lastUpdate = Date.now();
  state.isProcessingLLM = false;
  state.isTTSActive = false;
  state.ignoreTranscriptsUntil = 0;
  
  // Clear display
  state.displayText = '';
  state.displaySource = null;
  state.wordTimestamps = null;
  state.lastWordIndex = -1;
  
  // Clear conversation history
  state.conversationHistory = [];
}

/**
 * Cleanup speech state
 */
export function cleanupSpeech(state: SpeechState): void {
  stopWordDisplay(state);
  // Event listeners are managed by the bus
}

/**
 * Generate speech string for URL hash
 * Returns the current display text (50 characters)
 */
export function generateSpeechString(state: SpeechState): string {
  // Return the current display text (always 50 characters when active)
  return state.displayText || '';
}
